import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { connsPublic } from '@cfg/axiosConfig.tsx';
import type { RootState } from '@store/store';
import { PaymentMethod } from '@eshop/types/payment.types.ts';
import { logDebug, logError } from '@tools/LogsHandler.tsx';

interface PaymentParams {
  smid?: number;
  takeaway?: boolean;
}

interface PaymentsState {
  methods: PaymentMethod[];
  status: 'idle' | 'loading' | 'failed';
  selectedMethodId?: number;
  shippingMethodId?: number;
  lastParams?: PaymentParams;
}

const arePaymentParamsEqual = (
  left: PaymentParams | undefined,
  right: PaymentParams | undefined,
): boolean =>
  left?.smid === right?.smid &&
  left?.takeaway === right?.takeaway;

const initialState: PaymentsState = {
  methods: [],
  status: 'idle',
  selectedMethodId: undefined,
  shippingMethodId: undefined,
  lastParams: undefined,
};

export const fetchPaymentMethods = createAsyncThunk<
  PaymentMethod[],
  PaymentParams | undefined,
  { state: RootState; rejectValue: string }
>(
  'payments/fetchMethods',
  async (params, { rejectWithValue }) => {
    try {
      logDebug('fetchPaymentMethods.requested', { params });
      const { data } = await connsPublic<PaymentMethod[]>('eshop:payment:methods').get(params);
      logDebug('fetchPaymentMethods.succeeded', {
        params,
        methodCount: Array.isArray(data) ? data.length : 0,
      });
      return data;
    } catch (err) {
      logError('fetchPaymentMethods.failed', { params, error: err });
      return rejectWithValue(`Failed to load payment methods: ${err}`);
    }
  },
  {
    condition: (params, { getState }) => {
      const { payments } = getState();
      return !(
        payments.lastParams &&
          arePaymentParamsEqual(payments.lastParams, params) &&
          payments.methods.length
      );
    },
  }
);

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    setSelectedMethod: (state, action: PayloadAction<number | undefined>) => {
      state.selectedMethodId = action.payload;
    },
    setShippingMethodId: (state, action: PayloadAction<number | undefined>) => {
      state.shippingMethodId = action.payload;
    },
    clearMethods: (state) => {
      state.methods = [];
      state.lastParams = undefined;
      state.shippingMethodId = undefined;
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentMethods.pending, (state, action) => {
        state.status = 'loading';
        state.lastParams = action.meta.arg;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        if (!arePaymentParamsEqual(state.lastParams, action.meta.arg)) {
          logDebug('fetchPaymentMethods.discarded', {
            reason: 'stale_params',
            responseParams: action.meta.arg,
            currentParams: state.lastParams,
            methodCount: action.payload.length,
          });
          return;
        }
        state.status = 'idle';
        state.methods = action.payload;
        if (!action.payload.some((m) => m.id === state.selectedMethodId)) {
          state.selectedMethodId = undefined;
        }
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        if (!arePaymentParamsEqual(state.lastParams, action.meta.arg)) {
          logDebug('fetchPaymentMethods.discarded', {
            reason: 'stale_params',
            responseParams: action.meta.arg,
            currentParams: state.lastParams,
            outcome: 'rejected',
          });
          return;
        }
        state.status = 'failed';
      });
  },
});

export const { setSelectedMethod, setShippingMethodId, clearMethods } = paymentsSlice.actions;

export const selectPaymentMethods = (state: RootState) => state?.payments?.methods ?? [];
export const selectPaymentStatus = (state: RootState) => state?.payments?.status ?? 'idle';
export const selectPaymentLoading = (state: RootState) => state?.payments?.status === 'loading';
export const selectPaymentLastParams = (state: RootState) => state?.payments?.lastParams;
export const selectSelectedPaymentMethod = (state: RootState) =>
  (state?.payments?.methods ?? []).find((m) => m.id === state?.payments?.selectedMethodId);

export default paymentsSlice.reducer;
